import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { facilitiesAPI } from '@/lib/api';
import { FacilityUpcomingMatches } from '@/components/FacilityUpcomingMatches';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, MapPin, Phone, Mail, Globe, Clock,
  CheckCircle, Coffee, ShowerHead, Car, Wifi, Accessibility, Package, Cross, Lock,
  Ruler, Settings, Navigation, CalendarDays, Users,
} from 'lucide-react';
import { resolveMediaUrl } from '@/lib/utils';
import { FacilitiesMap } from '@/components/FacilityMap';
import { FacilityBookingDialog, formatDateStr } from '@/components/FacilityBookingDialog';
import { FieldCard } from '@/components/FacilityFields';

// ── Constants ──────────────────────────────────────────────────────────────────

const AMENITY_MAP = {
  bar: { icon: Coffee, label: 'Bar / Ristoro' },
  showers: { icon: ShowerHead, label: 'Docce' },
  lockers: { icon: Lock, label: 'Spogliatoi' },
  parking: { icon: Car, label: 'Parcheggio' },
  wifi: { icon: Wifi, label: 'Wi-Fi' },
  first_aid: { icon: Cross, label: 'Primo Soccorso' },
  wheelchair_access: { icon: Accessibility, label: 'Accesso Disabili' },
  equipment_rental: { icon: Package, label: 'Noleggio Attrezzatura' },
};

const DAYS_MAP = {
  mon: 'Lunedì', tue: 'Martedì', wed: 'Mercoledì', thu: 'Giovedì',
  fri: 'Venerdì', sat: 'Sabato', sun: 'Domenica',
};

const DAYS_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// ── Main Component ─────────────────────────────────────────────────────────────

/**
 * What players see: the fields with "Prenota", their own bookings here and the info.
 * Owners and managers only get a button to the management page.
 */
export const FacilityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [facility, setFacility] = useState(null);
  const [fields, setFields] = useState([]);
  const [owner, setOwner] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [bookingField, setBookingField] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [cancelingId, setCancelingId] = useState(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchFacility = useCallback(async () => {
    try {
      const res = await facilitiesAPI.getById(id);
      setFacility(res.data.facility);
      setFields(res.data.fields || []);
      setOwner(res.data.owner);
      setIsManager(res.data.is_manager);
    } catch (error) {
      toast.error('Struttura non trovata');
      navigate('/facilities');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchMyBookings = useCallback(async () => {
    if (!user) return;
    try {
      const res = await facilitiesAPI.getMyBookings();
      const today = formatDateStr(new Date());
      setMyBookings((res.data || []).filter((b) => b.facility_id === id && b.date >= today));
    } catch {
      // ponytail: the list is a convenience, the page works without it
    }
  }, [id, user]);

  useEffect(() => { fetchFacility(); }, [fetchFacility]);
  useEffect(() => { fetchMyBookings(); }, [fetchMyBookings]);

  const openBooking = (field) => {
    setBookingField(field?.field_id || null);
    setShowBookingDialog(true);
  };

  const handleCancelBooking = async (bookingId) => {
    if (cancelingId !== bookingId) {
      setCancelingId(bookingId);
      return;
    }
    try {
      await facilitiesAPI.cancelBooking(bookingId);
      toast.success('Prenotazione annullata');
      fetchMyBookings();
    } catch (error) {
      toast.error('Errore nella cancellazione');
    } finally {
      setCancelingId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-56 w-full rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  if (!facility) return null;

  const activeAmenities = Object.entries(facility.amenities || {}).filter(([, v]) => v);
  const openingHours = facility.opening_hours || {};

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="facility-detail-page">
      <Button variant="ghost" className="-mb-2" onClick={() => navigate('/facilities')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Strutture
      </Button>

      {/* ── Hero header ─────────────────────────────────────── */}
      <div className="relative rounded-xl overflow-hidden">
        {facility.cover_image_url ? (
          <img
            src={resolveMediaUrl(facility.cover_image_url)}
            alt={facility.name}
            className="w-full h-56 md:h-72 object-cover"
          />
        ) : (
          <div className="w-full h-56 md:h-72 bg-muted flex items-center justify-center">
            <Building2 className="h-16 w-16 text-muted-foreground/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold">{facility.name}</h1>
            {facility.is_verified && (
              <Badge className="bg-green-600 text-white border-0">
                <CheckCircle className="h-3 w-3 mr-1" />
                Verificata
              </Badge>
            )}
          </div>
          {(facility.address || facility.city) && (
            <p className="flex items-center gap-1 mt-2 text-white/90 text-sm">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="truncate">{facility.city || facility.address}</span>
            </p>
          )}
        </div>

        {isManager && (
          <Button size="sm" className="absolute top-4 right-4" asChild data-testid="manage-facility-btn">
            <Link to={`/facilities/${id}/dashboard`}>
              <Settings className="h-4 w-4 mr-1" />
              Gestisci struttura
            </Link>
          </Button>
        )}
      </div>

      <FacilityUpcomingMatches facilityId={id} />

      {/* ── Your bookings here ──────────────────────────────── */}
      {myBookings.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Le tue prenotazioni qui
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {myBookings.map((b) => (
              <div key={b.booking_id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/40">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {new Date(`${b.date}T00:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {' · '}{b.start_time}–{b.end_time}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {b.field_name}
                    {b.match_id && (
                      <>
                        {' · '}
                        <Link to={`/matches/${b.match_id}`} className="text-primary hover:underline">
                          {b.match_title || 'Vai alla partita'}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
                <Button
                  variant={cancelingId === b.booking_id ? 'destructive' : 'ghost'}
                  size="sm"
                  onClick={() => handleCancelBooking(b.booking_id)}
                >
                  {cancelingId === b.booking_id ? 'Conferma' : 'Annulla'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Fields ──────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">
            Campi{fields.length > 0 && ` (${fields.length})`}
          </h2>
          {fields.length > 0 && (
            <Button variant="outline" size="sm" asChild data-testid="organize-match-btn">
              <Link to={`/matches/new?facility=${id}`}>
                <Users className="h-4 w-4 mr-1" />
                Organizza una partita qui
              </Link>
            </Button>
          )}
        </div>
        {fields.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Ruler className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Nessun campo registrato</p>
              {isManager ? (
                <Button variant="link" asChild>
                  <Link to={`/facilities/${id}/dashboard#fields`}>Aggiungi i campi da Gestisci struttura</Link>
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">La struttura non ha ancora aggiunto i propri campi</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => (
              <FieldCard key={field.field_id} field={field} onBook={openBooking} />
            ))}
          </div>
        )}
      </section>

      {/* ── Info ────────────────────────────────────────────── */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Informazioni</h2>

        {facility.description && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm whitespace-pre-wrap">{facility.description}</p>
            </CardContent>
          </Card>
        )}

        {facility.lat != null && facility.lng != null && (
          <Card className="overflow-hidden">
            <FacilitiesMap facilities={[facility]} fitToPins className="h-56 w-full" />
            <CardContent className="p-3 flex items-center gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground truncate flex-1">{facility.address || facility.city}</span>
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${facility.lat},${facility.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Navigation className="h-4 w-4 mr-1" />
                  Indicazioni
                </a>
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Contacts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contatti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {facility.phone && (
                <a href={`tel:${facility.phone}`} className="flex items-center gap-2 text-sm hover:text-primary">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {facility.phone}
                </a>
              )}
              {facility.email && (
                <a href={`mailto:${facility.email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {facility.email}
                </a>
              )}
              {facility.website && (
                <a href={facility.website} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm hover:text-primary">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  {facility.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {!facility.phone && !facility.email && !facility.website && (
                <p className="text-sm text-muted-foreground">Nessun contatto inserito</p>
              )}
              {owner && (
                <p className="text-xs text-muted-foreground pt-1">Gestita da {owner.name}</p>
              )}
            </CardContent>
          </Card>

          {/* Opening Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Orari
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(openingHours).length > 0 ? (
                <div className="space-y-1.5">
                  {DAYS_ORDER.map(day => {
                    const h = openingHours[day];
                    if (!h) return null;
                    return (
                      <div key={day} className="flex items-center justify-between text-sm">
                        <span className="font-medium w-24">{DAYS_MAP[day]}</span>
                        {h.closed ? (
                          <span className="text-muted-foreground">Chiuso</span>
                        ) : (
                          <span>{h.open} — {h.close}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Orari non specificati</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Amenities */}
        {activeAmenities.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Servizi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {activeAmenities.map(([key]) => {
                  const am = AMENITY_MAP[key];
                  if (!am) return null;
                  const Icon = am.icon;
                  return (
                    <div key={key} className="flex items-center gap-2 p-2 rounded-lg border">
                      <Icon className="h-4 w-4 text-primary shrink-0" />
                      <span className="text-sm">{am.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <FacilityBookingDialog
        facilityId={id}
        fields={fields}
        isManager={isManager}
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        fieldId={bookingField}
        onBooked={fetchMyBookings}
      />
    </div>
  );
};

export default FacilityDetailPage;
